import { Button, Callout, TextField } from "@bruntworkwear-apps/ui";
import styles from "../UploadForm.module.css";
import type { RateBandsState } from "../useRateBands";

interface RateBandsViewProps {
  bands: RateBandsState;
  flatRate: string;
  disabled: boolean;
}

/**
 * The optional dated landing rates.
 *
 * Empty is the normal state and the default: with no rows, every line takes the
 * flat rate above, exactly as it always did. Rows only appear when freight was
 * genuinely re-quoted part-way through the close.
 *
 * Presentation only - every rule lives in {@link useRateBands}.
 */
export function RateBandsView({ bands, flatRate, disabled }: RateBandsViewProps) {
  const hasRows = bands.drafts.length > 0;

  return (
    <div className={styles.bands}>
      <p className={styles.bandsHint}>
        {hasRows
          ? "Lines are costed at the rate covering their posting date. Anything outside every " +
            `band falls back to the flat rate of ${flatRate}.`
          : `Freight re-quoted mid-month? Add a dated rate. With none, every line takes the flat ` +
            `rate of ${flatRate}.`}
      </p>

      {bands.drafts.map((draft, index) => (
        <div key={draft.id} className={styles.band}>
          <div className={styles.bandFields}>
            <TextField
              label="From"
              type="date"
              value={draft.startsOn}
              disabled={disabled}
              onChange={(event) => bands.update(draft.id, { startsOn: event.target.value })}
            />
            <TextField
              label="To"
              type="date"
              value={draft.endsOn}
              disabled={disabled}
              hint={index === 0 ? "Inclusive." : undefined}
              onChange={(event) => bands.update(draft.id, { endsOn: event.target.value })}
            />
            <TextField
              label="Rate"
              prefix="$"
              inputMode="decimal"
              value={draft.rate}
              disabled={disabled}
              onChange={(event) => bands.update(draft.id, { rate: event.target.value })}
            />
            <TextField
              label="Label"
              value={draft.label}
              placeholder="H1"
              disabled={disabled}
              hint={index === 0 ? "Optional. Shown on the export." : undefined}
              onChange={(event) => bands.update(draft.id, { label: event.target.value })}
            />
          </div>

          <Button
            variant="ghost"
            disabled={disabled}
            onClick={() => bands.remove(draft.id)}
            aria-label={`Remove the ${draft.label || `band starting ${draft.startsOn}`} rate`}
          >
            Remove
          </Button>

          {bands.errors[draft.id] && (
            <span className={styles.bandError} role="alert">
              {bands.errors[draft.id]}
            </span>
          )}
        </div>
      ))}

      {bands.scheduleError && <Callout tone="danger">{bands.scheduleError}</Callout>}

      <div className={styles.bandActions}>
        <Button variant="secondary" onClick={bands.add} disabled={disabled}>
          Add a dated rate
        </Button>
        {!hasRows && bands.canSeed && (
          <Button variant="ghost" onClick={bands.seedSemiMonthly} disabled={disabled}>
            Split the current month at the 15th
          </Button>
        )}
        {hasRows && (
          <Button variant="ghost" onClick={bands.reset} disabled={disabled}>
            Use one flat rate
          </Button>
        )}
      </div>
    </div>
  );
}
